import type {
    AIActions,
    AIResponse,
    Bookmark,
    BookmarkCreatePayload,
    ContentStructure,
    DictionaryResponse,
    Flashcard,
    FlashcardCreatePayload,
    FlashcardUpdatePayload,
    GetPrintJobPayload,
    Note,
    NoteCreatePayload,
    NoteUpdatePayload,
    Position,
    PrintJob,
    PrintPagesPayload,
    PrintPagesResult,
    SearchResponse,
    SessionInitData,
    ShareLinkResponse,
    TranslateResponse,
} from '@publicala/delfino';

declare global {
    interface Window {
        __E2E_STANDALONE_MODE__?: boolean;
        __SEARCH_API_MOCK_RESPONSE__?: SearchResponse;
    }
}

/**
 * Checks if E2E standalone mode is active.
 * When active, API methods should skip hostBridge calls and return mock data.
 */
export function isE2EMockActive(): boolean {
    return window.__E2E_STANDALONE_MODE__ === true;
}

// =============================================================================
// MOCK DEFAULTS
// Centralized default mock data for all APIs that return values.
// These are used when window.__*_MOCK_RESPONSE__ is not set.
// =============================================================================

/**
 * Default mock for SearchAPI.search()
 */
export const DEFAULT_SEARCH_MOCK: SearchResponse = {
    results: [],
};

/**
 * Default mock for ContentAPI.getContentStructure()
 */
export const DEFAULT_CONTENT_MOCK: ContentStructure = {
    fileType: 'pdf',
    cover: '',
    url: '',
    files_urls: [],
    files_info: [],
    tableOfContents: [],
    articles: [],
};

/**
 * Default mock for SessionAPI.initialize()
 */
export const DEFAULT_SESSION_MOCK: SessionInitData = {
    modes: {
        preview: false,
        embedded: false,
        exhibition: null,
    },
    user: {
        id: 'mock-user-id',
        isGuest: false,
        isAnonymous: false,
    },
    tenant: {
        id: 'mock-tenant-id',
        lang: 'en',
        exitUrl: 'https://example.com/exit',
        hostLocation: 'https://example.com',
    },
    issue: {
        id: 'mock-issue-id',
        fileType: 'pdf',
        name: 'Mock Issue',
        description: 'Mock description',
        descriptionRawText: 'Mock description raw',
        tags: [],
        publicationDate: '2024-01-01',
        issueFullUrl: 'https://example.com/issue',
        lang: 'en',
        shareUrl: 'https://example.com/share',
        isBuyable: false,
    },
    settings: {
        reader: { layout: 'single' },
        ui: { promoteApp: null, hiddenElements: [] },
        user: {},
    },
    features: {
        tts: { enabled: false, driver: 'browser' },
        chineseVoice: false,
        search: true,
        notes: true,
        flashcards: true,
        aiIntegrations: false,
        concurrencyLimit: false,
        copyEnabled: true,
    },
    lastLocation: null,
    manifestVersion: '1.0.0',
};

/**
 * Default mock for NotesAPI.list()
 */
export const DEFAULT_NOTES_LIST_MOCK: Note[] = [];

/**
 * Default mock for BookmarksAPI.list()
 */
export const DEFAULT_BOOKMARKS_LIST_MOCK: Bookmark[] = [];

/**
 * Creates a mock Bookmark for BookmarksAPI.store()
 * @param payload - The bookmark creation payload
 */
export function createMockBookmark(payload: BookmarkCreatePayload): Bookmark {
    return {
        id: `mock-bookmark-${payload.uuid}`,
        uuid: payload.uuid,
        location: payload.location,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}

/**
 * Default mock for NotesAPI.createShareLink()
 */
export const DEFAULT_SHARE_LINK_MOCK: ShareLinkResponse = {
    uuid: 'mock-share-uuid',
    url: 'https://example.com/notes/follow/mock-share-uuid',
};

/**
 * Creates a mock Note for NotesAPI.store()
 * @param payload - The note creation payload
 * @param position - The built position object
 */
export function createMockNote(payload: Omit<NoteCreatePayload, 'position'>, position: Position): Note {
    return {
        id: 'mock-note-id',
        uuid: payload.uuid,
        color: payload.color,
        colorSlug: payload.colorSlug,
        totalText: payload.totalText,
        totalLength: payload.totalLength,
        position,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}

/**
 * Creates a mock Note for NotesAPI.update()
 * @param noteId - The note ID being updated
 * @param data - The update data
 */
export function createMockUpdatedNote(noteId: string, data: NoteUpdatePayload['data']): Note {
    return {
        id: noteId,
        uuid: 'mock-uuid',
        // TODO: Remove non-null assertions once @publicala/delfino makes color and colorSlug required in NoteUpdatePayload.data
        color: data.color!,
        colorSlug: data.colorSlug!,
        totalText: 'Updated note',
        totalLength: 12,
        position: { type: 'pdf', startPage: 1 },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}

/**
 * Creates a mock print-pages result for PrintAPI.requestPrintPages()
 * Echoes the requested range so the reader UI can be exercised without a backend.
 * @param payload - The print-pages request
 */
export function createPrintPagesMock(payload: PrintPagesPayload): PrintPagesResult {
    return {
        jobId: `mock-${payload.source}-${payload.pageFrom}-${payload.pageTo}`,
    };
}

/**
 * Creates a mock print job for PrintAPI.getPrintJob(), ready on the first ask
 * so the reader's "ready" notice can be exercised without a backend.
 * @param payload - The job being asked about
 */
export function createPrintJobMock(payload: GetPrintJobPayload): PrintJob {
    return { jobId: payload.jobId, status: 'ready', url: 'about:blank' };
}

/**
 * Default mock for FlashcardsAPI.list()
 */
export const DEFAULT_FLASHCARDS_LIST_MOCK: Flashcard[] = [];

/**
 * Creates a mock Flashcard for FlashcardsAPI.store()
 * @param payload - The flashcard creation payload (without nested position)
 * @param position - The built position object (undefined when created from the panel)
 */
export function createMockFlashcard(
    payload: Omit<FlashcardCreatePayload, 'position'>,
    position: Position | undefined
): Flashcard {
    return {
        id: `mock-flashcard-${payload.uuid}`,
        uuid: payload.uuid,
        color: payload.color,
        colorSlug: payload.colorSlug,
        frontText: payload.frontText,
        backText: payload.backText,
        sourceText: payload.sourceText,
        sourceTextLength: payload.sourceTextLength,
        aiGenerated: payload.aiGenerated ?? false,
        position,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}

/**
 * Creates a mock Flashcard for FlashcardsAPI.update()
 * @param flashcardId - The flashcard ID being updated
 * @param data - The update data
 */
export function createMockUpdatedFlashcard(flashcardId: string, data: FlashcardUpdatePayload['data']): Flashcard {
    return {
        id: flashcardId,
        uuid: 'mock-uuid',
        color: data.color ?? '#03a9f4',
        colorSlug: data.colorSlug,
        frontText: data.frontText ?? 'Updated front',
        backText: data.backText ?? 'Updated back',
        aiGenerated: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}

/**
 * Default mock for AIIntegrationsAPI.generateResponse()
 */
export const DEFAULT_AI_GENERATE_MOCK: AIResponse = {
    data: ['Mock AI response'],
    uuid: 'mock-ai-uuid',
};

/**
 * Default mock for AIIntegrationsAPI.listActions()
 */
export const DEFAULT_AI_ACTIONS_MOCK: AIActions = {
    summarize: 'Summarize',
    explain: 'Explain',
    expand: 'Expand',
};

/**
 * Default mock for DictionaryAPI.lookup()
 */
export const DEFAULT_DICTIONARY_MOCK: DictionaryResponse = {
    word: '',
    entries: [],
};

/**
 * Default mock for ListenAPI.synthesizeSpeech()
 */
export function createDefaultListenMock(): Blob {
    return new Blob([], { type: 'audio/mpeg' });
}

/**
 * Default mock for TranslateAPI.translate()
 */
export const DEFAULT_TRANSLATE_MOCK: TranslateResponse = {
    translations: [],
};
